import { marketSearchApi, Site } from '@envato/foundation-types'
import get from 'lodash-es/get'
import last from 'lodash-es/last'
import * as React from 'react'
import * as titleize from 'titleize'

import filterParamsConfig from '../../../common/constants/filterParamsConfig'
import { ILocationParams } from '../../../common/reducers/reactRouterLocation'
import { hasFeature } from '../../../common/utils/features'
import sitesConfig from '../../../config/sites'
import FilterDiscounts from './filterDiscounts'
import FilterCategory from './filterCategory'
import FilterMinResolution from './filterMinResolution'
import FilterMultiselect from './filterMultiselect/'
import FilterProperties from './filterProperties'
import FilterRadio from './filterRadio/filterRadio'
import FilterSoftwareVersion from './filterSoftwareVersion'
import FiltersRange from './filtersRange/'
import FiltersSales from './filtersSales/'
import FiltersSize from './filtersSize/'
import FilterText from './filterText/'
import SearchFilters from './searchFilters'

interface IProps {
  aggregations: marketSearchApi.IAggregations
  locationParams: ILocationParams
  currentSite: Site
  makeSearchLink: (params: ILocationParams, currentSite?: Site) => string
  totalHits: number
  loading: boolean
  location: any
  experiments: any
  toggleSidebar: () => void
  updateActiveFiltersCount: (count: number) => void
}

const sortAlphabetically = (options?: marketSearchApi.IFilterOption[]) => {
  return (options || []).sort((opt1, opt2) => (opt1.key < opt2.key ? -1 : 1))
}

const hasAggregation = (
  items: marketSearchApi.IFilterOption[] | undefined
): boolean => {
  return !!items && items.filter(item => item && item.count > 0).length > 0
}

const sortByColors = (options: marketSearchApi.IFilterOption[]) => {
  const order = [
    'pink',
    'red',
    'orange',
    'yellow',
    'green',
    'teal',
    'blue',
    'purple',
    'brown',
    'black',
    'grey',
    'white'
  ]
  return sortBy(options, order, 'key')
}

const sortByTempo = (options: marketSearchApi.IFilterOption[]) => {
  const order = ['very-slow', 'slow', 'medium', 'upbeat', 'fast', 'very-fast']
  return sortBy(options, order, 'key')
}

const sortBy = (items: any[], order: string[], key: string) => {
  return (items || [])
    .filter(item => order.indexOf(item[key]) >= 0)
    .sort((i1, i2) => order.indexOf(i1[key]) - order.indexOf(i2[key]))
}

type FilterOptions =
  | marketSearchApi.IFilterOption
  | marketSearchApi.INumberlessFilterOption

const withOption = (
  options: marketSearchApi.IFilterOption[],
  option:
    | marketSearchApi.IFilterOption
    | marketSearchApi.INumberlessFilterOption
): FilterOptions[] => {
  return options ? [option, ...options] : []
}

const withShowAllOption = (
  options: marketSearchApi.IFilterOption[]
): FilterOptions[] => {
  return withOption(options, {
    description: null,
    key: '',
    label: 'Show all'
  })
}

const withTotal = (
  options?: marketSearchApi.IFilterOption[]
): FilterOptions[] => {
  if (!options) {
    return []
  }
  const total = options.reduce((totalValue, curr) => totalValue + curr.count, 0)

  const totalOption = {
    count: total,
    description: null,
    key: ''
  }
  return withOption(options, totalOption)
}

const withSameLabelsAsKeys = (
  options?: marketSearchApi.IFilterOption[] | null
) => {
  return (options || []).map(option => ({
    ...option,
    label: option.key
  }))
}

const withCategoryLabels = (options?: marketSearchApi.IFilterOption[]) => {
  return (options || []).map(option => {
    const path = option.key.split('/')
    const name = last(path) || ''
    const label = titleize(name.replace('-', ' '))

    return {
      ...option,
      label
    }
  })
}

const hasChildCategories = (
  locationParams: ILocationParams,
  categories?: marketSearchApi.IFilterOption[]
): boolean => {
  const currentCategory = get(locationParams, 'category', '')
  const currentOrChildren = (categories || []).filter(category =>
    category.key.startsWith(currentCategory)
  )
  return currentOrChildren.length > 1
}

const getSiteFilterOrder = (currentSite: Site) => {
  return get(sitesConfig, `${currentSite}.filterOrder`, null)
}

const categoryFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterCategory,
    id: 'category',
    expanded: true,
    isActive: get(aggregations, 'category.length') > 0,
    props: {
      scrollable: false,
      type: 'category',
      items: withOption(withCategoryLabels(aggregations.category), {
        description: null,
        key: '',
        label: 'All categories',
        count: get(aggregations, 'category_root_count.count')
      })
    }
  }
}

const tagsFilterConfig = ({
  aggregations,
  experiments,
  locationParams,
  loading,
  currentSite,
  location
}: IProps) => {
  const collapseTagsFeature = hasFeature(
    'collapse-tags-unless-bottom-category',
    currentSite,
    location,
    experiments
  )
  const categoryHasChildren = hasChildCategories(
    locationParams,
    aggregations.category
  )
  const shouldCollapseTagFilterForExperiment =
    collapseTagsFeature && (categoryHasChildren || loading)
  const shouldCollapseTagFilter = shouldCollapseTagFilterForExperiment

  return {
    Component: FilterMultiselect,
    id: 'tags',
    expanded: !shouldCollapseTagFilter,
    isActive: get(aggregations, 'tags.length') > 0,
    props: {
      type: 'tags',
      items: aggregations.tags,
      toLowerCasedLabel: filterParamsConfig.tags.toLowerCasedLabel
    }
  }
}

const filesIncludedFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterMultiselect,
    id: 'files-included',
    title: 'Files Included',
    expanded: true,
    isActive: get(aggregations, 'files_included.length') > 0,
    props: {
      type: 'files_included',
      items: aggregations.files_included,
      labels: filterParamsConfig.files_included.labels
    }
  }
}

const priceFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FiltersRange,
    id: 'price',
    expanded: true,
    isActive: get(aggregations, 'cost.sum') > 0,
    props: {
      paramName: 'price',
      name: 'price',
      type: 'number',
      prefix: '$',
      range: {
        min: get(aggregations, 'cost.min') / 100,
        max: get(aggregations, 'cost.max') / 100
      }
    }
  }
}

const salesFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FiltersSales,
    id: 'sales',
    expanded: true,
    isActive: true,
    props: {
      type: 'sales',
      items: sortAlphabetically(aggregations.sales_count)
    }
  }
}

const ratingFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'rating',
    expanded: true,
    isActive: get(aggregations, 'rating.length') > 0,
    title: 'Rating',
    props: {
      paramName: 'rating_min',
      items: sortAlphabetically(aggregations.rating),
      labels: filterParamsConfig.rating_min.labels
    }
  }
}

const orientationFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'orientation',
    expanded: true,
    isActive: get(aggregations, 'orientation.length') > 0,
    props: {
      paramName: 'orientation',
      items: withTotal(sortAlphabetically(aggregations.orientation)),
      labels: filterParamsConfig.orientation.labels
    }
  }
}

const dateAddedFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'date-added',
    title: 'Date Added',
    expanded: true,
    isActive: hasAggregation(get(aggregations, 'date', [])),
    props: {
      paramName: 'date',
      items: aggregations.date,
      labels: filterParamsConfig.date.labels
    }
  }
}

const sizesFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FiltersSize,
    id: 'sizes',
    expanded: true,
    isActive: get(aggregations, 'size.length') > 0,
    props: {
      items: sortAlphabetically(aggregations.size)
    }
  }
}

const colorsFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterMultiselect,
    id: 'colors',
    expanded: true,
    isActive: get(aggregations, 'colors.length') > 0,
    props: {
      type: 'colors',
      items: sortByColors(aggregations.colors)
    }
  }
}

const softwareVersionFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterSoftwareVersion,
    id: 'software-version',
    expanded: true,
    isActive: get(aggregations, '__internal_software_versions.length') > 0,
    props: {
      type: 'platform',
      separator: ' ',
      scrollable: true,
      items: withOption(
        withSameLabelsAsKeys(aggregations.__internal_software_versions),
        {
          description: null,
          key: '',
          label: 'All software versions',
          count: get(aggregations, 'platform_root_count.count')
        }
      )
    }
  }
}

const discountsFilterConfig = ({ aggregations }: IProps) => {
  const discountsCount = get(aggregations, 'discount_count.count') || 0

  return {
    Component: FilterDiscounts,
    id: 'discounted-only',
    expanded: true,
    isActive: true,
    title: 'On Sale',
    props: {
      discountsCount: discountsCount
    }
  }
}

const minResolutionFilterConfig = ({
  aggregations,
  loading,
  totalHits
}: IProps) => {
  return {
    Component: FilterMinResolution,
    id: 'resolution',
    expanded: true,
    isActive: get(aggregations, 'resolution.length') > 0,
    title: 'Minimum Resolution',
    props: {
      paramName: 'resolution_min',
      loading,
      items: withOption(aggregations.resolution, {
        description: null,
        key: '',
        label: 'All',
        count: totalHits
      })
    }
  }
}

const frameRateFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'frame-rate',
    expanded: true,
    isActive: get(aggregations, 'frame_rate.length') > 0,
    props: {
      paramName: 'frame_rate',
      items: withShowAllOption(aggregations.frame_rate),
      labels: filterParamsConfig.frame_rate.labels
    }
  }
}

const compatibleWithFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterMultiselect,
    id: 'compatible-with',
    expanded: true,
    isActive: get(aggregations, 'compatible_with.length') > 0,
    props: {
      type: 'compatible_with',
      items: withSameLabelsAsKeys(aggregations.compatible_with)
    }
  }
}

const requiresPluginFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'requires-plugins',
    expanded: true,
    isActive: get(aggregations, 'requires_plugins.length') > 0,
    title: 'Requires Plugins',
    props: {
      paramName: 'requires_plugins',
      items: withShowAllOption(aggregations.requires_plugins),
      labels: filterParamsConfig.requires_plugins.labels
    }
  }
}

const lengthFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FiltersRange,
    id: 'length',
    expanded: true,
    isActive: get(aggregations, 'length.sum') > 0,
    props: {
      paramName: 'length',
      name: 'length',
      type: 'time',
      range: {
        min: get(aggregations, 'length.min'),
        max: get(aggregations, 'length.max')
      }
    }
  }
}

const vocalsFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterMultiselect,
    id: 'vocals',
    title: 'Vocals',
    expanded: true,
    isActive: get(aggregations, 'vocals_in_audio.length') > 0,
    props: {
      type: 'vocals_in_audio',
      items: aggregations.vocals_in_audio,
      labels: filterParamsConfig.vocals_in_audio.labels
    }
  }
}

const tempoFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'tempo',
    expanded: true,
    isActive: get(aggregations, 'tempo.length') > 0,
    title: 'Tempo',
    props: {
      paramName: 'tempo',
      items: withShowAllOption(sortByTempo(aggregations.tempo)),
      labels: filterParamsConfig.tempo.labels
    }
  }
}

const polygonsFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterText,
    id: 'polygons',
    expanded: true,
    isActive: aggregations.poly_count,
    props: {
      paramName: 'poly_count',
      name: 'Polygons',
      placeholder: `${get(aggregations, 'poly_count.avg')}, ${get(
        aggregations,
        'poly_count.min'
      )}-${get(aggregations, 'poly_count.max')}`
    }
  }
}

const propertiesFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterProperties,
    id: 'properties',
    expanded: true,
    isActive:
      get(aggregations, 'alpha.count') > 0 ||
      get(aggregations, 'looped.count') > 0,
    title: 'Properties',
    props: {
      alphaCount: get(aggregations, 'alpha.count'),
      loopedCount: get(aggregations, 'looped.count')
    }
  }
}

const includeProAffiliatedFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'include-pro-affiliated',
    expanded: true,
    isActive: get(aggregations, 'include_pro_affiliated.length') > 0,
    title: 'Include P.R.O Music',
    props: {
      items: get(aggregations, 'include_pro_affiliated'),
      paramName: 'include_pro_affiliated',
      labels: filterParamsConfig.include_pro_affiliated.labels,
      defaultSelectedValue: 'true'
    }
  }
}

const fileFormatsFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterMultiselect,
    id: 'file-formats',
    expanded: true,
    isActive: get(aggregations, 'file_formats.length') > 0,
    title: 'File Formats',
    props: {
      type: 'file_formats',
      items: aggregations.file_formats,
      labels: filterParamsConfig.file_formats.labels
    }
  }
}

const dateUpdatedFilterConfig = ({ aggregations }: IProps) => {
  return {
    Component: FilterRadio,
    id: 'date-updated',
    title: 'Date Updated',
    expanded: true,
    isActive: hasAggregation(get(aggregations, 'date_updated', [])),
    props: {
      paramName: 'date_updated',
      items: aggregations.date_updated,
      labels: filterParamsConfig.date_updated.labels
    }
  }
}

const getUsedFiltersCount = (urlParams: any): number => {
  const configParams = Object.keys(filterParamsConfig)

  return Object.keys(urlParams)
    .filter(param => configParams.includes(param) && param !== 'sort_by')
    .reduce((acc, current) => acc + urlParams[current].split(',').length, 0)
}

const activeSortedFilters = (filters: any[], currentSite: Site): any[] => {
  const activeFilters = filters.filter(filter => filter.isActive)
  const defaultFilterOrder = filters.map(filter => filter.id)
  const filterOrder = getSiteFilterOrder(currentSite) || defaultFilterOrder
  return sortBy(activeFilters, filterOrder, 'id')
}

const SearchFiltersContainer = (props: IProps) => {
  const {
    locationParams,
    currentSite,
    makeSearchLink,
    totalHits,
    toggleSidebar,
    updateActiveFiltersCount,
    loading,
    location
  } = props

  const hasDateUpdatedFeature = hasFeature(
    'dateUpdatedFilter',
    currentSite,
    location
  )

  const filters = [
    categoryFilterConfig(props),
    filesIncludedFilterConfig(props),
    tagsFilterConfig(props),
    priceFilterConfig(props),
    discountsFilterConfig(props),
    salesFilterConfig(props),
    ratingFilterConfig(props),
    orientationFilterConfig(props),
    dateAddedFilterConfig(props),
    ...(hasDateUpdatedFeature ? [dateUpdatedFilterConfig(props)] : []),
    sizesFilterConfig(props),
    colorsFilterConfig(props),
    softwareVersionFilterConfig(props),
    minResolutionFilterConfig(props),
    frameRateFilterConfig(props),
    compatibleWithFilterConfig(props),
    requiresPluginFilterConfig(props),
    lengthFilterConfig(props),
    vocalsFilterConfig(props),
    tempoFilterConfig(props),
    polygonsFilterConfig(props),
    propertiesFilterConfig(props),
    includeProAffiliatedFilterConfig(props),
    fileFormatsFilterConfig(props)
  ]

  updateActiveFiltersCount(getUsedFiltersCount(locationParams))

  return (
    <div>
      <SearchFilters
        locationParams={locationParams}
        activeFilters={activeSortedFilters(filters, currentSite)}
        currentSite={currentSite}
        makeSearchLink={makeSearchLink}
        toggleSidebar={toggleSidebar}
        totalHits={totalHits}
        loading={loading}
      />
    </div>
  )
}

export default SearchFiltersContainer
